import React from 'react';
import { Link } from 'react-router-dom';

// Slug d'ancre pour les titres (utilisé par le sommaire des articles)
export const headingSlug = (text) => String(text || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  .slice(0, 80);

// Rendu Markdown minimal et sûr (aucun HTML brut injecté) :
// ## titre, ### sous-titre, - liste, 1. liste numérotée, **gras**, [texte](lien), paragraphes

const renderLink = (text, href, key) => {
  if (href.startsWith('/')) {
    return <Link key={key} to={href} className="text-yellow-400 hover:underline">{text}</Link>;
  }
  if (/^https?:\/\//i.test(href)) {
    return <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="text-yellow-400 hover:underline">{text}</a>;
  }
  return text; // protocoles non autorisés (javascript:, data:…) affichés en texte
};

function renderInline(text, keyPrefix = 'i') {
  const parts = [];
  const pattern = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let match;
  while ((match = pattern.exec(text))) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const key = `${keyPrefix}-${match.index}`;
    parts.push(match[1] !== undefined
      ? <strong key={key} className="text-white font-semibold">{renderInline(match[1], key)}</strong>
      : renderLink(match[2], match[3], key));
    last = pattern.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export function parseBlocks(source = '') {
  const blocks = [];
  let list = null;
  let paragraph = null;
  for (const raw of source.replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.trim();
    const item = /^(?:[-*]|(\d+)\.)\s+(.*)$/.exec(line);
    if (item) {
      paragraph = null;
      const ordered = item[1] !== undefined;
      if (!list || list.ordered !== ordered) {
        list = { type: 'list', ordered, items: [] };
        blocks.push(list);
      }
      list.items.push(item[2]);
      continue;
    }
    list = null;
    if (!line) { paragraph = null; continue; }
    if (line.startsWith('## ') || line.startsWith('### ')) {
      paragraph = null;
      blocks.push({ type: line.startsWith('### ') ? 'h3' : 'h2', text: line.replace(/^#+\s+/, '') });
    } else if (paragraph) {
      paragraph.text += ` ${line}`; // lignes consécutives = même paragraphe
    } else {
      paragraph = { type: 'p', text: line };
      blocks.push(paragraph);
    }
  }
  return blocks;
}

// Titres ## / ### d'un article, pour le sommaire
export const extractHeadings = (source = '') =>
  parseBlocks(source)
    .filter(b => b.type === 'h2' || b.type === 'h3')
    .map(b => ({ level: b.type === 'h2' ? 2 : 3, text: b.text, id: headingSlug(b.text) }));

export default function Markdown({ source }) {
  const blocks = parseBlocks(source);
  return (
    <div className="text-gray-300 text-base sm:text-lg leading-relaxed space-y-4">
      {blocks.map((block, i) => {
        const key = `b${i}`;
        if (block.type === 'h2') return <h2 key={key} id={headingSlug(block.text)} className="text-xl sm:text-2xl font-bold text-white pt-4 scroll-mt-24">{renderInline(block.text, key)}</h2>;
        if (block.type === 'h3') return <h3 key={key} id={headingSlug(block.text)} className="text-lg sm:text-xl font-semibold text-white pt-2 scroll-mt-24">{renderInline(block.text, key)}</h3>;
        if (block.type === 'list') {
          const ListTag = block.ordered ? 'ol' : 'ul';
          return (
            <ListTag key={key} className={`${block.ordered ? 'list-decimal' : 'list-disc'} pl-5 sm:pl-6 space-y-1.5 marker:text-yellow-500`}>
              {block.items.map((item, j) => <li key={j}>{renderInline(item, `${key}-${j}`)}</li>)}
            </ListTag>
          );
        }
        return <p key={key}>{renderInline(block.text, key)}</p>;
      })}
    </div>
  );
}
